/**
 * Thin wrapper around WebGazer (CDN script + local MediaPipe assets).
 * Gaze runs entirely in the browser — no frame upload.
 */

import { ACCURACY_STARE_MS } from './webgazerCalibrationState';
import {
  webGazerFaceMeshAssetBase,
  webGazerScriptUrl,
} from './webgazerLoadPath';
import { precisionFromStoredPoints } from './webgazerPrecision';

export type GazeListener = (
  gaze: {
    x: number;
    y: number;
    eyeLeftWidth?: number;
    eyeRightWidth?: number;
  } | null
) => void;


export interface WebGazerFaceTracker {
  getPositions?: () => number[][] | null;
}

export interface WebGazerLike {
  begin: () => Promise<WebGazerLike> | WebGazerLike;
  end: () => Promise<void> | void;
  pause: () => WebGazerLike;
  resume: () => WebGazerLike;
  isReady: () => boolean;
  setGazeListener: (
    fn:
      | ((
          data: {
            x: number;
            y: number;
            eyeFeatures?: {
              left?: { width?: number; height?: number };
              right?: { width?: number; height?: number };
            } | null;
          } | null,
          elapsed: number
        ) => void)
      | undefined
  ) => WebGazerLike;
  showVideoPreview: (show: boolean) => WebGazerLike;
  showPredictionPoints: (show: boolean) => WebGazerLike;
  showFaceOverlay?: (show: boolean) => WebGazerLike;
  showFaceFeedbackBox?: (show: boolean) => WebGazerLike;
  applyKalmanFilter: (use: boolean) => WebGazerLike;
  saveDataAcrossSessions: (save: boolean) => WebGazerLike;
  setRegression: (name: string) => WebGazerLike;
  clearData?: () => void;
  getStoredPoints?: () => [number[], number[]] | null;
  /** TFFaceMesh tracker — exposes last MediaPipe landmark positions. */
  getTracker?: () => WebGazerFaceTracker | null;
  params?: {
    faceMeshSolutionPath?: string;
    storingPoints?: boolean;
    [key: string]: unknown;
  };
}

/**
 * Last Face Mesh landmark array from WebGazer's tracker ([x,y,z] × 468+).
 * Null when the tracker has not produced a face yet.
 * Tolerates both tuple and `{x,y,z}` keypoint shapes from TF/MediaPipe.
 */
export function readWebGazerFaceLandmarks(): number[][] | null {
  try {
    const tracker = getGlobal()?.getTracker?.() as
      | (WebGazerFaceTracker & { positionsArray?: unknown })
      | null
      | undefined;
    const raw = tracker?.getPositions?.() ?? tracker?.positionsArray ?? null;
    if (!raw || !Array.isArray(raw) || raw.length < 455) {
      return null;
    }
    const first = raw[0] as unknown;
    if (Array.isArray(first) && typeof (first as number[])[0] === 'number') {
      return raw as number[][];
    }
    if (first && typeof first === 'object' && 'x' in (first as object)) {
      return (raw as Array<{ x: number; y: number; z?: number }>).map((kp) => [
        kp.x,
        kp.y,
        kp.z ?? 0,
      ]);
    }
    return null;
  } catch {
    return null;
  }
}

declare global {
  interface Window {
    webgazer?: WebGazerLike;
    saveDataAcrossSessions?: boolean;
  }
}

let loadPromise: Promise<WebGazerLike> | null = null;
let listener: GazeListener | null = null;

function getGlobal(): WebGazerLike | undefined {
  return typeof window !== 'undefined' ? window.webgazer : undefined;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (getGlobal()?.begin) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load WebGazer from ${src}`));
    document.head.appendChild(script);
  });
}

/** Point Face Mesh at locally served wasm/js (see scripts/copy-webgazer-assets.mjs). */
function applyFaceMeshSolutionPath(wg: WebGazerLike): void {
  const base = webGazerFaceMeshAssetBase();
  try {
    if (!wg.params) {
      (wg as { params: Record<string, unknown> }).params = {};
    }
    // WebGazer 3 TFFaceMesh reads params.faceMeshSolutionPath at detector init.
    (wg.params as { faceMeshSolutionPath?: string }).faceMeshSolutionPath = base;
  } catch {
    /* older builds may lack params */
  }
}

/** Load WebGazer from pinned CDN; Face Mesh assets stay on this origin. */
export async function loadWebGazer(): Promise<WebGazerLike> {
  if (getGlobal()?.begin) {
    applyFaceMeshSolutionPath(getGlobal()!);
    return getGlobal()!;
  }
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    await loadScript(webGazerScriptUrl());

    const wg = getGlobal();
    if (!wg?.begin) {
      throw new Error('WebGazer failed to initialize on window');
    }

    applyFaceMeshSolutionPath(wg);

    try {
      window.saveDataAcrossSessions = false;
      wg.saveDataAcrossSessions?.(false);
      wg.setRegression?.('ridge');
      wg.applyKalmanFilter?.(true);
      wg.showVideoPreview?.(false);
      wg.showPredictionPoints?.(false);
    } catch {
      // Older builds may lack some setters
    }

    return wg;
  })();

  try {
    return await loadPromise;
  } catch (err) {
    loadPromise = null;
    throw err;
  }
}

export async function startWebGazer(onGaze: GazeListener): Promise<void> {
  const wg = await loadWebGazer();
  listener = onGaze;
  wg.setGazeListener((data) => {
    if (!listener) return;
    if (!data || data.x == null || data.y == null) {
      listener(null);
      return;
    }
    const leftW = data.eyeFeatures?.left?.width;
    const rightW = data.eyeFeatures?.right?.width;
    listener({
      x: data.x,
      y: data.y,
      ...(typeof leftW === 'number' ? { eyeLeftWidth: leftW } : {}),
      ...(typeof rightW === 'number' ? { eyeRightWidth: rightW } : {}),
    });
  });

  // Avoid a second getUserMedia/begin() after calibration — that freezes the gaze dot.
  let ready = false;
  try {
    ready = Boolean(wg.isReady?.());
  } catch {
    ready = false;
  }
  if (!ready) {
    await Promise.resolve(wg.begin());
  } else {
    try {
      wg.resume?.();
    } catch {
      /* ignore */
    }
  }

  // Face-box validation + visible gaze prediction dot during the exam.
  setExamFaceWatch(true);
  try {
    wg.showPredictionPoints?.(true);
    const dot = document.getElementById('webgazerGazeDot');
    if (dot) {
      dot.style.display = 'block';
      dot.style.opacity = '0.85';
      dot.style.zIndex = '99999';
      dot.style.pointerEvents = 'none';
    }
  } catch {
    /* ignore */
  }
}

/**
 * Run WebGazer's eyes-in-box check during the exam (head/face away → red border)
 * without showing the video preview UI.
 */
export function setExamFaceWatch(enabled: boolean): void {
  const wg = getGlobal();
  if (!wg) return;
  try {
    if (!wg.params) {
      (wg as { params: Record<string, unknown> }).params = {};
    }
    const params = wg.params as { showFaceFeedbackBox?: boolean };
    params.showFaceFeedbackBox = enabled;
    const box = document.getElementById('webgazerFaceFeedbackBox');
    if (box) {
      // Keep element in DOM for border updates; hide visually.
      box.style.display = enabled ? 'block' : 'none';
      box.style.opacity = '0';
      box.style.pointerEvents = 'none';
    }
  } catch {
    /* ignore */
  }
}

export function pauseWebGazer(): void {
  try {
    getGlobal()?.pause?.();
  } catch {
    /* ignore */
  }
}

export function resumeWebGazer(): void {
  try {
    getGlobal()?.resume?.();
  } catch {
    /* ignore */
  }
}

export async function stopWebGazer(): Promise<void> {
  listener = null;
  setExamFaceWatch(false);
  setStoringPoints(false);
  setWebGazerChromeClickThrough(false);
  clearPlottingCanvas();
  removePlottingCanvas();
  const wg = getGlobal();
  if (!wg) return;
  try {
    wg.setGazeListener(undefined as unknown as never);
  } catch {
    /* ignore */
  }
  try {
    await Promise.resolve(wg.end?.());
  } catch {
    /* ignore */
  }
}

export function isWebGazerReady(): boolean {
  try {
    return Boolean(getGlobal()?.isReady?.());
  } catch {
    return Boolean(getGlobal());
  }
}

/** Toggle video preview, prediction points, and face overlay/feedback when available. */
export function setCalibrationPreview(enabled: boolean): void {
  const wg = getGlobal();
  if (!wg) return;
  try {
    wg.showVideoPreview?.(enabled);
    wg.showPredictionPoints?.(enabled);
    wg.showFaceOverlay?.(enabled);
    wg.showFaceFeedbackBox?.(enabled);
  } catch {
    /* older builds may lack some setters */
  }
  // Gaze dot is z-index 99999 and sits on the point you look at — must not steal clicks.
  setWebGazerChromeClickThrough(enabled);
  if (!enabled) {
    clearPlottingCanvas();
  }
}

/** IDs WebGazer mounts on document.body (must not intercept calibration clicks). */
export const WEBGAZER_CHROME_ELEMENT_IDS = [
  'webgazerVideoContainer',
  'webgazerGazeDot',
  'webgazerFaceOverlay',
  'webgazerFaceFeedbackBox',
] as const;

const CLICKTHROUGH_BODY_CLASS = 'qs-webgazer-clickthrough';
const CLICKTHROUGH_STYLE_ID = 'qs-webgazer-clickthrough-style';

function ensureClickThroughStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CLICKTHROUGH_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CLICKTHROUGH_STYLE_ID;
  // Gaze dot is created async after begin(); CSS covers late-mounted nodes.
  style.textContent = `
    body.${CLICKTHROUGH_BODY_CLASS} #webgazerVideoContainer,
    body.${CLICKTHROUGH_BODY_CLASS} #webgazerGazeDot,
    body.${CLICKTHROUGH_BODY_CLASS} #webgazerFaceOverlay,
    body.${CLICKTHROUGH_BODY_CLASS} #webgazerFaceFeedbackBox {
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * When clickThrough is true, WebGazer chrome ignores pointer events so calibration
 * buttons underneath remain clickable (WebGazer still learns from document clicks).
 */
export function setWebGazerChromeClickThrough(clickThrough: boolean): void {
  if (typeof document === 'undefined') return;
  ensureClickThroughStyles();
  document.body.classList.toggle(CLICKTHROUGH_BODY_CLASS, clickThrough);
  const value = clickThrough ? 'none' : 'auto';
  for (const id of WEBGAZER_CHROME_ELEMENT_IDS) {
    const el = document.getElementById(id);
    if (el) el.style.pointerEvents = value;
  }
}

export function setStoringPoints(enabled: boolean): void {
  const wg = getGlobal();
  if (!wg) return;
  try {
    if (!wg.params) {
      (wg as { params: Record<string, unknown> }).params = {};
    }
    const params = wg.params;
    if (params) params.storingPoints = enabled;
  } catch {
    /* ignore */
  }
}

export function getStoredPoints(): [number[], number[]] | null {
  try {
    return getGlobal()?.getStoredPoints?.() ?? null;
  } catch {
    return null;
  }
}

export function clearWebGazerData(): void {
  try {
    getGlobal()?.clearData?.();
  } catch {
    /* ignore */
  }
}

/** Official WebGazer demo canvas — required for storePoints during accuracy. */
export const PLOTTING_CANVAS_ID = 'plotting_canvas';

/**
 * WebGazer CDN draws blue sample dots via `#plotting_canvas` before `storePoints`.
 * Without this element, getContext throws and zero samples are stored → 0% accuracy.
 * Keep the canvas invisible so sample dots never litter the UI.
 */
export function ensurePlottingCanvas(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;

  let canvas = document.getElementById(PLOTTING_CANVAS_ID) as HTMLCanvasElement | null;
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.id = PLOTTING_CANVAS_ID;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'fixed';
    canvas.style.left = '0';
    canvas.style.top = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.opacity = '0';
    canvas.style.visibility = 'hidden';
    canvas.style.zIndex = '1';
    document.body.appendChild(canvas);
  }

  const width = typeof window !== 'undefined' ? window.innerWidth : 640;
  const height = typeof window !== 'undefined' ? window.innerHeight : 480;
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  return canvas;
}

/** Erase drawn sample dots and hide the canvas (call after accuracy / on leave). */
export function clearPlottingCanvas(): void {
  if (typeof document === 'undefined') return;
  const canvas = document.getElementById(PLOTTING_CANVAS_ID) as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  canvas.style.opacity = '0';
  canvas.style.visibility = 'hidden';
}

/** Remove plotting canvas from the DOM entirely. */
export function removePlottingCanvas(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(PLOTTING_CANVAS_ID)?.remove();
}

/**
 * Collect stare samples then score accuracy (0–100).
 * Call after click calibration; uses storingPoints + getStoredPoints.
 */
export async function measureCalibrationAccuracy(
  stareMs = ACCURACY_STARE_MS
): Promise<number> {
  ensurePlottingCanvas();
  setStoringPoints(true);
  await new Promise((r) => setTimeout(r, stareMs));
  setStoringPoints(false);
  const pts = getStoredPoints();
  const score = precisionFromStoredPoints(
    pts,
    window.innerWidth,
    window.innerHeight
  );
  clearPlottingCanvas();
  return score;
}
